/**
 * Material Tickets List Component
 * Displays and manages material tickets for a job
 */

import { useState, useEffect } from 'react';
import { TicketCard } from './TicketCard';
import type { MaterialTicket } from './types';
import { toast } from '@/lib/utils/toast';

interface MaterialTicketsListProps {
  jobId: string;
}

export function MaterialTicketsList({ jobId }: MaterialTicketsListProps) {
  const [tickets, setTickets] = useState<MaterialTicket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchTickets = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/jobs/${jobId}/tickets`);

      if (!response.ok) {
        throw new Error('Failed to load tickets');
      }

      const data = await response.json();
      setTickets(data.tickets || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tickets');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const handleDelete = async (ticketId: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/jobs/${jobId}/tickets/${ticketId}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete ticket');
      }

      // Refresh tickets after deletion
      fetchTickets();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete ticket');
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-8 text-neutral-600">
        Loading tickets...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm text-red-800">{error}</p>
      </div>
    );
  }

  if (!tickets || tickets.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-neutral-600">No material tickets yet</p>
        <p className="text-sm text-neutral-500 mt-2">
          Click &ldquo;Add Ticket&rdquo; to record your first waste haul
        </p>
      </div>
    );
  }

  // Filter tickets by search query
  const filteredTickets = tickets.filter(
    (ticket) =>
      ticket.hauler_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.facility_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.materials.some((m) =>
        m.material_type.toLowerCase().includes(searchQuery.toLowerCase())
      )
  );

  // Group tickets by date
  const ticketsByDate = filteredTickets.reduce<Record<string, MaterialTicket[]>>((groups, ticket) => {
    const date = new Date(ticket.ticket_date).toISOString().split('T')[0] || 'unknown';
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date]!.push(ticket);
    return groups;
  }, {});

  const sortedDates = Object.keys(ticketsByDate).sort((a, b) => b.localeCompare(a));

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder="Search tickets..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {/* Tickets grouped by date */}
      {sortedDates.length === 0 ? (
        <div className="text-center py-8 text-neutral-600">
          No tickets match your search
        </div>
      ) : (
        sortedDates.map((date) => (
          <div key={date}>
            <h3 className="text-sm font-medium text-neutral-600 mb-2">
              {formatDate(date)}
            </h3>

            <div className="space-y-2">
              {ticketsByDate[date]?.map((ticket) => (
                <TicketCard
                  key={ticket.id}
                  ticket={ticket}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
